<!-- eslint-disable no-restricted-syntax -->
<template>
  <div id="ngo-admin-add">
    <section class="container mt-5 mb-5 ngo-form-page">
      <button type="button" class="back-button mt-3" @click="handleBack">
        <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
        <span>{{ operations === 'create' ? 'Back to NGO Dashboard' : 'Back to NGO Details' }}</span>
      </button>

      <div class="form-hero mx-2">
        <div class="form-hero__copy">
          <p class="hero-kicker">
            {{ operations === 'create' ? 'NGO Registration' : 'NGO Update' }}
          </p>
          <h1>
            {{
              operations === 'create'
                ? 'Register a new organization'
                : 'Update organization details'
            }}
          </h1>
          <p class="hero-description">
            {{
              operations === 'create'
                ? 'Enter the organization name and email. Add other profile details and supporting files when available.'
                : 'Update the organization name and email, and add other details when available.'
            }}
          </p>
        </div>
        <div class="form-hero__stats">
          <div class="hero-stat">
            <span class="hero-stat__label">Mode</span>
            <strong class="hero-stat__value">{{
              operations === 'create' ? 'New registration' : 'Update organization'
            }}</strong>
          </div>
          <div class="hero-stat">
            <span class="hero-stat__label">Coverage</span>
            <strong class="hero-stat__value">Profile, Team, Assets</strong>
          </div>
        </div>
      </div>

      <div class="form-shell">
        <ValidationObserver v-slot="{ passes }">
          <form @submit.prevent="passes(() => submit(form))" id="ngo-form" novalidate>
            <div class="form-row">
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Name of Organization"
                  width="100%"
                  rules="required"
                  v-model="form.name"
                  label="Name of Organization"
                  name="Name of Organization"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Email Address"
                  width="100%"
                  rules="required|email"
                  v-model="form.email"
                  label="Email Address"
                  type="email"
                  name="Email Address"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Username"
                  width="100%"
                  v-model="form.username"
                  label="Username"
                  type="text"
                  name="Username"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Tracking ID"
                  width="100%"
                  v-model="form.tracking_id"
                  label="Tracking ID"
                  type="text"
                  name="Tracking ID"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="File No."
                  width="100%"
                  v-model="form.file_no"
                  label="File No."
                  type="text"
                  name="File No."
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="2023, 2024, 2025"
                  width="100%"
                  v-model="form.years_renewed"
                  label="Years Renewed"
                  type="text"
                  name="Years Renewed"
                />
                <small class="d-block text-muted mt-1">
                  Enter years separated by commas. This field is optional.
                </small>
              </div>
              <div class="col-12">
                <base-textarea
                  :rows="2"
                  placeholder="Address"
                  width="100%"
                  v-model="form.address"
                  label="Address"
                  name="Address"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Year of Establishment"
                  width="100%"
                  v-model="form.establishment_year"
                  label="Year of Establishment"
                  type="number"
                  name="Year of Establishment"
                />
              </div>
              <div class="col-12 col-md-6">
                <base-select
                  height="3rem"
                  placeholder="Select Organization type"
                  width="100%"
                  :items="organizationType"
                  v-model="form.type"
                  label="Select Organization Type"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="col-md-6">
                <base-select
                  height="3rem"
                  placeholder="Is it an umbrella organization"
                  width="100%"
                  :items="polar"
                  v-model="form.is_umbrella_organization"
                  label="Is it an umbrella organization?"
                />
              </div>
              <div v-if="form.is_umbrella_organization === 'yes'" class="col-md-12">
                <div class="mt-4 border p-3 mb-2 border-radius">
                  <h6 class="fw-bold mb-3">Sub Organizations</h6>

                  <div
                    v-for="(organization, index) in form.umbrella_org"
                    :key="index"
                    class="card mb-3"
                  >
                    <div class="card-body">
                      <base-input
                        height="3rem"
                        :placeholder="`Sub organization ${index + 1}`"
                        width="100%"
                        v-model="form.umbrella_org[index]"
                        :label="`Sub Organization ${index + 1}`"
                        :name="`Sub Organization ${index + 1}`"
                      />

                      <button
                        v-if="form.umbrella_org.length > 1"
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        @click="removeUmbrellaOrganization(index)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-success btn-sm"
                    @click="addUmbrellaOrganization"
                  >
                    + Add Sub Organization
                  </button>
                </div>
              </div>
            </div>

            <br />
            <div class="sub-head">Principal Contact Person(s)</div>
            <p class="text-muted">All contact person details are optional.</p>

            <div
              v-for="(contact, index) in form.contact_persons"
              :key="index"
              class="card p-5 mb-3"
            >
              <h2>Contact Person {{ index + 1 }}</h2>
              <div class="form-row" id="contact_person">
                <div class="col-md-12">
                  <base-input
                    height="3rem"
                    placeholder="First Name of contact person"
                    width="100%"
                    v-model="contact.full_name"
                    label="First Name of contact person"
                    name="First Name of contact person"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="col-md-6">
                  <base-input
                    height="3rem"
                    placeholder="Phone"
                    width="100%"
                    v-model="contact.phone_number"
                    label="Phone"
                    type="tel"
                  />
                </div>
                <div class="col-md-6">
                  <base-input
                    height="3rem"
                    placeholder="Email"
                    width="100%"
                    v-model="contact.email"
                    label="Email"
                    type="email"
                    name="Email"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="col-md-6">
                  <base-input
                    height="3rem"
                    placeholder="Position"
                    width="100%"
                    v-model="contact.position"
                    label="Position"
                  />
                </div>
                <div class="col-md-6">
                  <base-input
                    height="3rem"
                    placeholder="Address"
                    width="100%"
                    v-model="contact.address"
                    label="Address"
                    type="text"
                    :name="`Contact Address ${index + 1}`"
                  />
                </div>
              </div>
              <div class="d-flex align-items-center upload-preview-row">
                <div class="col-md-6 upload-sec">
                  <p>CV of Contact Person {{ index + 1 }}</p>
                  <input
                    type="file"
                    class="ml-1 mt-0"
                    accept="application/pdf,application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/*"
                    @change="handleContactPersonFileUpload($event, index)"
                  />
                </div>
                <div
                  v-if="contact.cvPreview || contact.cvName || (contact.cv && contact.cv.file_url)"
                  class="col-md-6"
                >
                  <img
                    v-if="contact.cvPreview"
                    :src="contact.cvPreview"
                    class="image object-fit-contain"
                    alt=""
                  />
                  <div v-else class="file-reflection">
                    <span class="file-reflection__label">Selected file</span>
                    <strong>{{ contact.cvName || getFileNameFromUrl(contact.cv.file_url) }}</strong>
                  </div>
                </div>
              </div>

              <!-- <pre>{{ form.contact_persons }}</pre> -->
              <div
                v-if="form.contact_persons.length > 1"
                class="d-flex justify-content-end contact-action-row"
              >
                <button @click="removeItem(contact.id)" type="button" class="btn btn-danger w-25">
                  Remove Item
                </button>
              </div>
            </div>
            <div v-if="form.contact_persons.length < 2" class="mt-1 add-contact-row">
              <button @click="addMore" type="button" class="btn btn-primary w-25">Add More</button>
            </div>

            <div class="form-row">
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Telephone"
                  width="100%"
                  v-model="form.phone_number"
                  label="Telephone"
                  name="Telephone"
                  type="number"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  height="3rem"
                  placeholder="Website URL"
                  width="100%"
                  v-model="form.web_url"
                  label="Web URL"
                  name="web url"
                  type="url"
                />
              </div>
            </div>

            <br />
            <br />
            <div class="sub-head">Organization</div>

            <base-textarea
              height="3rem"
              placeholder="Focus of Organization programmes in health"
              width="100%"
              v-model="form.organization_focus"
              label="Brief Bio of Organization"
              name="Brief Bio of Organization"
            />
            <div class="sub-head program-area-heading">
              <span>Program Area</span>
              <button type="button" class="program-area-create" @click="openProgramAreaModal()">
                <span class="program-area-create__icon" aria-hidden="true">
                  <i class="fas fa-plus"></i>
                </span>
                Create Program Area
              </button>
            </div>
            <p class="program-area-hint">
              Select your areas of focus, or create an area if you cannot find a match.
            </p>
            <!-- <pre>{{ tempArea }}</pre> -->
            <div class="">
              <multiselect
                v-model="tempArea"
                tag-placeholder="Create this program area"
                placeholder="Select program areas"
                label="name"
                track-by="id"
                :options="programs"
                :multiple="true"
                :taggable="true"
                @tag="openProgramAreaModal"
              ></multiselect>
            </div>
            <div class="sub-head mt-3">Head Office Location</div>

            <div class="form-row">
              <div class="col-md-6">
                <base-select
                  height="3rem"
                  placeholder="Select Zone"
                  width="100%"
                  :items="location"
                  v-model="selectedLocation"
                  label="HEAD OFFICE GEOGRAPHIC ZONE"
                  :track="'name'"
                />
              </div>
              <div class="col-md-6">
                <base-select
                  height="3rem"
                  placeholder="Select State"
                  width="100%"
                  :items="
                    selectedLocation && Array.isArray(selectedLocation.states)
                      ? selectedLocation.states
                      : []
                  "
                  v-model="form.state"
                  label="HEAD OFFICE STATE"
                  :disabled="!selectedLocation || !selectedLocation.name"
                />
              </div>
            </div>

            <div class="sub-head mt-3">Other Branches</div>
            <div class="form-row">
              <div class="col-md-12">
                <multiselect
                  v-model="form.branch"
                  placeholder="Select branch"
                  :options="states"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                ></multiselect>
              </div>
            </div>

            <div class="my-2">
              <div class="py-4 px-3 staff-count">
                <div class="staff-count__header">
                  <div>
                    <p class="staff-count__eyebrow">Workforce profile</p>
                    <h5 class="fw-bold mb-1">Staff Information</h5>
                  </div>
                  <p class="staff-count__copy">
                    Capture the total workforce, leadership split, and employment structure in one
                    concise block.
                  </p>
                </div>

                <div class="staff-groups">
                  <div class="staff-group staff-group--summary">
                    <h6>Total Staff</h6>
                    <label>Total</label>
                    <input
                      type="number"
                      class="form-control text-center"
                      v-model="form.staff_member.number_of_staffs"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div class="staff-group">
                    <h6>Senior Staff</h6>
                    <div class="staff-fields">
                      <div class="staff-field">
                        <label>Total</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_senior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div class="staff-field">
                        <label>Male</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_male_senior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div class="staff-field">
                        <label>Female</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_female_senior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="staff-group">
                    <h6>Junior Staff</h6>
                    <div class="staff-fields">
                      <div class="staff-field">
                        <label>Total</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_junior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div class="staff-field">
                        <label>Male</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_male_junior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div class="staff-field">
                        <label>Female</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_female_junior_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="staff-group">
                    <h6>Employment Type</h6>
                    <div class="staff-fields">
                      <div class="staff-field">
                        <label>Full Time</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_full_time_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div class="staff-field">
                        <label>Part Time</label>
                        <input
                          type="number"
                          class="form-control text-center"
                          v-model="form.staff_member.number_of_part_time_staffs"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">
                Assets Information (e.g - Landed property, Building, shares)
              </h6>

              <div v-for="(asset, index) in form.assets" :key="index" class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Asset Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="asset.name"
                      placeholder="e.g., Office Building"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Description</label>
                    <textarea
                      class="form-control"
                      v-model="asset.description"
                      rows="2"
                      placeholder="Brief description"
                    ></textarea>
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Address / Location</label>
                    <textarea
                      rows="2"
                      class="form-control"
                      v-model="asset.address"
                      placeholder="e.g., 24 Allen Avenue, Lagos"
                    ></textarea>
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Value (₦)</label>
                    <input
                      type="number"
                      step="0.01"
                      class="form-control"
                      v-model.number="asset.value"
                      placeholder="0.00"
                    />
                  </div>

                  <button
                    v-if="form.assets.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeAsset(index)"
                  >
                    Remove Asset
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addAsset">
                + Add Asset
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Physical Facility (Fixed assets)</h6>

              <div
                v-for="(facility, index) in form.physical_facilities"
                :key="index"
                class="card mb-3"
              >
                <div class="card-body">
                  <div class="d-flex facility-row">
                    <div class="mb-3 w-75 mr-2">
                      <label class="form-label fw-semibold">Facility Name</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="facility.facility_name"
                        placeholder="Name of facility "
                      />
                    </div>

                    <div class="mb-3 w-25">
                      <label class="form-label fw-semibold">Number</label>
                      <input
                        type="number"
                        step="0.01"
                        class="form-control"
                        v-model.number="facility.facility_number"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <button
                    v-if="form.physical_facilities.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeFacility(index)"
                  >
                    Remove Facility
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addFacility">
                + Add Facility
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Bankers</h6>

              <div v-for="(bank, index) in form.bankers" :key="index" class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Bank Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="bank.name"
                      placeholder="e.g.. bank name"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Bank Address / Location</label>
                    <textarea
                      rows="2"
                      class="form-control"
                      v-model="bank.address"
                      placeholder="e.g., bank address"
                    ></textarea>
                  </div>

                  <button
                    v-if="form.bankers.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeBanker(index)"
                  >
                    Remove Bank
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addBanker">
                + Add Bank
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Source Of Funding</h6>

              <div
                v-for="(source, index) in form.sources_of_funding"
                :key="index"
                class="card mb-3"
              >
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Source Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="source.source_name"
                      placeholder=""
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Description</label>
                    <textarea
                      class="form-control"
                      v-model="source.description"
                      rows="2"
                      placeholder="Brief description"
                    ></textarea>
                  </div>

                  <button
                    v-if="form.sources_of_funding.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeSource(index)"
                  >
                    Remove Source
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addSource">
                + Add Source
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Past Project</h6>

              <div v-for="(project, index) in form.past_projects" :key="index" class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">project Title</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="project.title"
                      placeholder="project title"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">project Funder</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="project.funder"
                      placeholder="project funder"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Duration</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="project.duration"
                      placeholder="duration"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Value (₦)</label>
                    <input
                      type="number"
                      step="0.01"
                      class="form-control"
                      v-model.number="project.amount"
                      placeholder="0.00"
                    />
                  </div>

                  <button
                    v-if="form.past_projects.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeProject(index)"
                  >
                    Remove Project
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addProject">
                + Add Project
              </button>
            </div>

            <!-- <div class="form-row">
            <div class="col-md-6">
              <base-input
                height="3rem"
                placeholder="No of Staff"
                width="100%"
                v-model="form.number_of_staffs"
                label="No of Staff"
                type="number"
              />
            </div>
            <div class="col-md-6">
              <base-input
                height="3rem"
                placeholder="Physical Facilites"
                width="100%"
                v-model="form.physical_facilities"
                label="Physical Facilites with no (e.g Offices x1, ..)"
              />
            </div>
          </div> -->

            <!-- <div class="form-row">
            <div class="col-md-4" id="letter_of_introduction">
              <p style="color: black; font-weight: bold">Letter of Introduction</p>
              <input
                type="file"
                class="ml-1 mt-0"
                accept="application/pdf,application/msword,
  application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/*"
                @change="fileSelected5(event)"
              />
            </div>
          </div>

          <base-input
            height="3rem"
            placeholder="Banker1"
            width="100%"
            v-model="banker1"
            label="Bankers and their full addresses"
            name="Bankers and their full addresses"
          />

          -->

            <base-textarea
              height="3rem"
              placeholder="Scope of Operation Coverage"
              width="100%"
              v-model="form.scope"
              label="Scope of Operation Coverage"
              name="Scope of Operation"
            />

            <base-textarea
              height="3rem"
              placeholder="Local Affiliation"
              width="100%"
              v-model="form.local_affiliation"
              label="Local Affiliation"
              name="Local Affiliation"
            />

            <base-textarea
              height="3rem"
              placeholder="international Affiliation"
              width="100%"
              v-model="form.international_affiliation"
              label="International Affiliation"
              name="International Affiliation"
            />

            <base-select
              height="3rem"
              placeholder="Is your organization working with others"
              width="100%"
              :items="polar"
              v-model="form.is_collaborative"
              label="Does your organization work with other organizations?"
            />

            <div v-if="form.is_collaborative === 'yes'" class="mt-4 border p-3 mb-4 border-radius">
              <div class="mb-3">
                <h6 class="fw-bold mb-1">Collaboration Partners</h6>
                <p class="mb-0 text-muted">Add the organizations this NGO currently works with.</p>
              </div>

              <div
                v-for="(collaboration, index) in form.collaboration_list"
                :key="index"
                class="card mb-3"
              >
                <div class="card-body">
                  <base-input
                    height="3rem"
                    :placeholder="`Collaboration partner ${index + 1}`"
                    width="100%"
                    v-model="form.collaboration_list[index]"
                    :label="`Collaboration Partner ${index + 1}`"
                    :name="`Collaboration Partner ${index + 1}`"
                  />

                  <button
                    v-if="form.collaboration_list.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeCollaboration(index)"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addCollaboration">
                + Add Partner
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Resource Persons</h6>

              <div v-for="(person, index) in form.resource_persons" :key="index" class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Source Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="person.name"
                      placeholder="name of resource person"
                    />
                  </div>

                  <div class="mb-3 d-flex align-items-center upload-preview-row">
                    <div class="col-md-6 upload-sec">
                      <p>CV of Resource person {{ index + 1 }}</p>
                      <input
                        type="file"
                        class="ml-1 mt-0"
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/*"
                        @change="handleResourcePersonFileUpload($event, index)"
                      />
                    </div>
                    <div
                      v-if="
                        person.cvPreview ||
                        person.cvName ||
                        (person.cv_url !== undefined && person.cv_url !== null)
                      "
                      class="col-md-6"
                    >
                      <img
                        v-if="person.cvPreview"
                        :src="person.cvPreview"
                        class="image object-fit-contain"
                        alt=""
                      />
                      <div v-else class="file-reflection">
                        <span class="file-reflection__label">Selected file</span>
                        <strong>{{ person.cvName || getFileNameFromUrl(person.cv_url) }}</strong>
                      </div>
                    </div>
                    <!-- <div class="col-md-6">
                    <label class="checkbox-wrapper checkbox-green d-flex align-items-center">
                      <input
                        type="checkbox"
                        class="text-success"
                        :checked="hasCV(person)"
                        disabled
                      />
                      <span class="ml-2">CV Uploaded</span>
                    </label>
                  </div> -->
                  </div>

                  <button
                    v-if="form.resource_persons.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeResourceperson(index)"
                  >
                    Remove Source
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addResourcePerson">
                + Add Source
              </button>
            </div>

            <div class="mt-4 border p-3 mb-4 border-radius">
              <h6 class="fw-bold mb-3">Publications</h6>

              <div v-for="(publication, index) in form.publications" :key="index" class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Publication Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="publication.name"
                      placeholder="e.g., publication name"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold">Publication Link</label>
                    <input
                      type="url"
                      class="form-control"
                      v-model="publication.link"
                      placeholder="https://example.com/publication"
                    />
                  </div>

                  <button
                    v-if="form.publications.length > 1"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removePublication(index)"
                  >
                    Remove Publication
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-success btn-sm" @click="addPublication">
                + Add Publication
              </button>
            </div>

            <!-- TOUR GUIDE -->
            <v-tour name="toursguide" :steps="steps">
              <template slot-scope="tour">
                <transition name="fade">
                  <v-step
                    v-if="tour.steps[tour.currentStep]"
                    :key="tour.currentStep"
                    :step="tour.steps[tour.currentStep]"
                    :previous-step="tour.previousStep"
                    :next-step="tour.nextStep"
                    :stop="tour.stop"
                    class="v-tour-step"
                    :skip="tour.skip"
                    :is-first="tour.isFirst"
                    :is-last="tour.isLast"
                    :labels="tour.labels"
                  >
                    <template>
                      <div slot="actions">
                        <button
                          v-if="tour.currentStep > 0"
                          @click="tour.previousStep"
                          class="btn btn-primary"
                        >
                          Previous step
                        </button>
                        <button @click="tour.nextStep" class="btn btn-primary">Next step</button>
                        <button
                          v-if="tour.currentStep === 2"
                          @click="tour.skip"
                          class="btn btn-primary"
                        >
                          Finish
                        </button>
                      </div>
                    </template>
                  </v-step>
                </transition>
              </template>
            </v-tour>
            <!-- TOUR GUIDE -->
            <br /><br /><br />

            <div class="d-flex justify-content-end gap-2 form-actions">
              <button
                type="submit"
                class="text-white bg-primary py-3 px-4 custom-button mt-4"
                :disabled="loader"
              >
                <div class="spinner-border" role="status" v-if="loader">
                  <span class="sr-only">Loading...</span>
                </div>
                <span v-else>{{ operations === 'create' ? 'Submitn Form' : 'Update Form ' }}</span>
              </button>
              <button
                v-if="operations === 'create'"
                type="button"
                class="text-white bg-secondary py-3 px-4 ml-4 custom-button mt-4"
                :disabled="draftLoader"
                @click="saveOrganizationToDraft"
              >
                <div class="spinner-border" role="status" v-if="draftLoader">
                  <span class="sr-only">Loading...</span>
                </div>
                <span v-else>Save to Draft</span>
              </button>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </section>
    <b-modal
      id="ngo-create-program-area"
      v-model="showProgramAreaModal"
      title="Create Program Area"
      content-class="ngo-program-modal"
      header-class="ngo-program-modal__header"
      body-class="ngo-program-modal__body"
      centered
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      :hide-header-close="savingProgramArea"
    >
      <template #modal-title>
        <span class="ngo-program-modal__eyebrow">Program Area</span>
        <span class="ngo-program-modal__title">Create a program area</span>
      </template>
      <div class="ngo-program-modal__intro">
        <span class="ngo-program-modal__icon" aria-hidden="true">
          <i class="fas fa-seedling"></i>
        </span>
        <p>Give your area of focus a clear name and a short description to help others find it.</p>
      </div>
      <form
        class="ngo-program-modal__form"
        :aria-busy="savingProgramArea"
        @submit.prevent="saveProgramArea"
      >
        <div class="ngo-program-modal__field">
          <label for="new-program-area-name">Program area name</label>
          <input
            id="new-program-area-name"
            v-model.trim="newProgramArea.name"
            class="form-control"
            placeholder="e.g. Community nutrition"
            required
            autofocus
            :disabled="savingProgramArea"
          />
        </div>
        <div class="ngo-program-modal__field">
          <label for="new-program-area-description">Description</label>
          <textarea
            id="new-program-area-description"
            v-model.trim="newProgramArea.description"
            class="form-control"
            placeholder="Describe the activities or health needs this area covers..."
            rows="4"
            required
            :disabled="savingProgramArea"
          ></textarea>
        </div>
        <p v-if="programAreaError" class="ngo-program-modal__error" role="alert">
          {{ programAreaError }}
        </p>
        <div
          v-if="savingProgramArea"
          class="ngo-program-modal__loading"
          role="status"
          aria-live="polite"
        >
          <span class="spinner-border" aria-hidden="true"></span>
          <div>
            <strong>Saving program area...</strong>
            <p>Please wait while we save and select your new area.</p>
          </div>
        </div>
        <p v-else class="ngo-program-modal__note">
          <i class="fas fa-check-circle" aria-hidden="true"></i>
          Your new area will be selected for this organization.
        </p>
        <div class="ngo-program-modal__actions">
          <button
            type="button"
            class="ngo-program-modal__cancel"
            :disabled="savingProgramArea"
            @click="showProgramAreaModal = false"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="ngo-program-modal__save"
            :disabled="savingProgramArea || !newProgramArea.name || !newProgramArea.description"
          >
            <span
              v-if="savingProgramArea"
              class="spinner-border spinner-border-sm mr-2"
              aria-hidden="true"
            ></span>
            <i v-else class="fas fa-plus mr-2" aria-hidden="true"></i>
            {{ savingProgramArea ? 'Saving...' : 'Create Program Area' }}
          </button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
// import _baseSelect from '../../../../components/FormInputs/_base-select.vue';
// import _baseTextarea from '../../../../components/FormInputs/_base-textarea.vue';
import { mapGetters } from 'vuex';
import { createProgramArea } from '../../../admin/config/api-modules/api.programAreas';
import {
  getProgramArea,
  getOrganization,
  updateOrganizationDetail,
  addOrganization as ADD_ORGANIZATION,
  saveToDraft as SAVE_TO_DRAFT,
  // patchOrganization as UPDATAE_NGO,
} from '../../config/api-modules/api.organization';
import zones from '../../config/states_and_zones';

export default {
  // components: { _baseSelect, _baseTextarea },
  name: 'ngo-admin-add',
  data() {
    return {
      steps: [
        {
          target: '#letter_of_introduction',
          content:
            '<div>Upload your letter of introduction which requests registration to FMOH </div>',
        },
        {
          target: '#contact_person',
          content:
            '<div>You may upload supporting documents such as a staff CV, organization profile, and activity report </div>',
        },
        {
          target: '#letter_of_introduction',
          content:
            '<div>Upload your letter of introduction which requests registration to FMOH </div>',
        },
      ],
      operations: 'create',
      tourShown: '',
      form: {
        id: '',
        name: '',
        username: '',
        tracking_id: '',
        years_renewed: '',
        file_no: '',
        type: 'Non governmental Organization',
        program_area: null,
        email: '',
        address: '',
        establishment_year: '',
        state: '',
        geo_zone: '',
        branch: [],
        name_of_organization: '',
        is_umbrella_organization: '',
        cac_registration_year: '',
        cac_registration_number: '',
        cac_type: '',
        cac_postal_address: '',
        contact_persons: [
          {
            id: Date.now(),
            full_name: '',
            phone_number: '',
            email: '',
            address: '',
            position: '',
            cv: null,
            cvName: '',
            cvPreview: '',
          },
        ],
        // focus_organisation_programmes: '',
        organization_focus: '',
        web_url: '',
        source_of_funding: null,
        sources_of_funding: [],
        scope: '',
        bankers: [],
        physical_facilities: [],
        assets: [],
        other_assets: '',
        local_affiliation: '',
        international_affiliation: '',
        collaboration_list: [],
        resource_persons: [],
        past_projects: [],
        publications: [],
        list_past_publications: '',
        is_collaborative: 'no',
        umbrella_org: [],
        cac_registration_certificate: null,
        cv_of_regular_contact_person: null,
        activities_documentation: null,
        // resource_persons: '',
        // umbrella_org_file: null,
        regular_resource_persons: '',
        status: false,
        areas_of_focus: [],
        contact: [],
        staff_member: {
          number_of_staffs: 0,
          number_of_senior_staffs: 0,
          number_of_junior_staffs: 0,
          number_of_male_senior_staffs: 0,
          number_of_female_senior_staffs: 0,
          number_of_male_junior_staffs: 0,
          number_of_female_junior_staffs: 0,
          number_of_full_time_staffs: 0,
          number_of_part_time_staffs: 0,
        },
      },
      polar: ['yes', 'no'],
      type: ['For Profit', 'Non Profit'],
      zones: [
        'North Central',
        'North East',
        'North West',
        'South East',
        'South South',
        'South West',
      ],
      states: [
        'Niger',
        'Kogi',
        'Benue',
        'Plateau',
        'Nasarawa',
        'Kwara',
        'FCT',
        'Bauchi',
        'Borno',
        'Taraba',
        'Adamawa',
        'Gombe',
        'Yobe',
        'Zamfara',
        'Sokoto',
        'Kaduna',
        'Kebbi',
        'Katsina',
        'Kano',
        'Jigawa',
        'Enugu',
        'Imo',
        'Ebonyi',
        'Abia',
        'Anambra',
        'Bayelsa',
        'Akwa Ibom',
        'Edo',
        'Rivers',
        'Cross River',
        'Delta',
        'Oyo',
        'Ekiti',
        'Osun',
        'Ondo',
        'Lagos',
        'Ogun',
      ],
      source1: '',
      source2: '',
      source3: '',
      banker1: '',
      banker2: '',
      banker3: '',
      cac_registration_certificate: null,
      cv_of_regular_contact_person: null,
      activities_documentation: null,
      letter_of_introduction: null,
      umbrella_org_file: null,
      loader: false,
      draftLoader: false,
      programs: [],
      showProgramAreaModal: false,
      savingProgramArea: false,
      newProgramArea: { name: '', description: '' },
      programAreaError: '',
      zone: zones,
      program_area_loader: false,
      total: null,
      location: [
        {
          name: 'All',
          states: ['All'],
        },
        {
          name: 'North Central',
          states: ['All', 'BENUE', 'FCT', 'KOGI', 'KWARA', 'NASARAWA', 'NIGER', 'PLATEAU'],
        },
        {
          name: 'North East',
          states: ['All', 'ADAMAWA', 'BAUCHI', 'BORNO', 'GOMBE', 'TARABA', 'YOBE'],
        },
        {
          name: 'North West',
          states: ['All', 'JIGAWA', 'KADUNA', 'KANO', 'KATSINA', 'KEBBI', 'SOKOTO', 'ZAMFARA'],
        },
        {
          name: 'South East',
          states: ['ABIA', 'ANAMBRA', 'EBONYI', 'ENUGU', 'IMO'],
        },
        {
          name: 'South South',
          states: ['All', 'AKWA IBOM', 'BAYELSA', 'CROSS RIVER', 'DELTA', 'EDO', 'RIVERS'],
        },
        {
          name: 'South West',
          states: ['All', 'EKITI', 'LAGOS', 'OGUN', 'ONDO', 'OSUN', 'OYO'],
        },
      ],
      selectedLocation: { name: null, states: [] },
      loadedData: false,
      tempArea: [],
      savedDraft: null,
      contactList: [
        {
          id: 1,
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          address: '',
          position: '',
        },
      ],
      organizationType: [
        'International Partner',
        'INGO',
        'Faith-based NGO',
        'Local NGO',
        'Profit Non governmental Organization',
        'NON-Profit Non governmental Organization',
        'Unspecified',
      ],
      cacRegistrationPreview: '',
    };
  },
  watch: {
    selectedLocation(newLocation) {
      console.log(newLocation);
      this.form.geo_zone = newLocation?.name || '';

      if (!newLocation?.name) {
        this.form.state = '';
      }
    },
    'form.is_umbrella_organization': {
      handler(value) {
        if (
          value === 'yes' &&
          (!Array.isArray(this.form.umbrella_org) || !this.form.umbrella_org.length)
        ) {
          this.form.umbrella_org = [''];
          return;
        }

        if (value !== 'yes') {
          this.form.umbrella_org = [];
        }
      },
    },
    'form.is_collaborative': {
      handler(value) {
        if (
          value === 'yes' &&
          (!Array.isArray(this.form.collaboration_list) || !this.form.collaboration_list.length)
        ) {
          this.form.collaboration_list = [''];
          return;
        }

        if (value !== 'yes') {
          this.form.collaboration_list = [];
        }
      },
    },
  },
  computed: {
    ...mapGetters('auth', ['user', 'isLoggedIn', 'isAdmin']),
  },

  methods: {
    openProgramAreaModal(name = '') {
      this.newProgramArea = { name, description: '' };
      this.programAreaError = '';
      this.showProgramAreaModal = true;
    },
    async saveProgramArea() {
      const name = this.newProgramArea.name.trim();
      const description = this.newProgramArea.description.trim();
      if (this.savingProgramArea || !name || !description) return;

      this.programAreaError = '';
      const existing = this.programs.find(
        (program) => program.name.trim().toLowerCase() === name.toLowerCase()
      );
      if (existing) {
        this.programAreaError =
          'This program area already exists. Cancel and select it from the program area list.';
        return;
      }

      this.savingProgramArea = true;
      try {
        const response = await createProgramArea({ name, description });
        const created = response.data || response;
        if (created.id != null) {
          const program = { ...created, name: created.name || name };
          this.programs.push(program);
          this.tempArea.push(program);
        } else {
          await this.getPrograms();
          const program = this.programs.find(
            (item) => item.name.trim().toLowerCase() === name.toLowerCase()
          );
          if (program) this.tempArea.push(program);
        }
        this.showProgramAreaModal = false;
        this.$toast.show({ type: 'SUCCESS', message: 'Program area created successfully' });
      } catch (error) {
        this.programAreaError = 'Unable to create program area. Please try again.';
      } finally {
        this.savingProgramArea = false;
      }
    },
    handleBack() {
      if (this.operations === 'create') {
        this.$router.push('/ngo');
        return;
      }

      this.$router.go(-1);
    },
    addTag(newTag) {
      const tag = {
        name: newTag,
        code: newTag.substring(0, 2) + Math.floor(Math.random() * 10000000),
      };
      this.options.push(tag);
      this.value.push(tag);
    },
    addMore() {
      this.form.contact_persons.push(this.createEmptyContactPerson());
    },

    addFacility() {
      this.form.physical_facilities.push({
        facility_name: '',
        facility_number: null,
      });
    },

    removeFacility(index) {
      this.form.physical_facilities.splice(index, 1);
    },

    addAsset() {
      this.form.assets.push({
        name: '',
        description: '',
        address: '',
        value: null,
      });
    },

    removeAsset(index) {
      this.form.assets.splice(index, 1);
    },

    addBanker() {
      this.form.bankers.push({
        name: '',
        address: '',
      });
    },

    removeBanker(index) {
      this.form.bankers.splice(index, 1);
    },

    addSource() {
      this.form.sources_of_funding.push({
        source_name: '',
        description: '',
      });
    },

    removeSource(index) {
      this.form.sources_of_funding.splice(index, 1);
    },

    addProject() {
      this.form.past_projects.push({
        title: '',
        funder: '',
        duration: '',
        amount: null,
      });
    },

    removeProject(index) {
      this.form.past_projects.splice(index, 1);
    },

    addCollaboration() {
      this.form.collaboration_list.push('');
    },

    removeCollaboration(index) {
      this.form.collaboration_list.splice(index, 1);
    },

    addResourcePerson() {
      this.form.resource_persons.push({
        name: '',
        cv: null,
        cvName: '',
        cvPreview: '',
      });
    },

    removeResourceperson(index) {
      this.form.resource_persons.splice(index, 1);
    },

    addPublication() {
      this.form.publications.push({
        name: '',
        link: '',
      });
    },

    removePublication(index) {
      this.form.publications.splice(index, 1);
    },

    handleContactPersonFileUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;

      const isImage = file.type && file.type.startsWith('image/');
      const preview = isImage ? URL.createObjectURL(file) : '';

      this.$set(this.form.contact_persons[index], 'cv', file);
      this.$set(this.form.contact_persons[index], 'cvName', file.name);
      this.$set(this.form.contact_persons[index], 'cvPreview', preview);
    },

    getFileNameFromUrl(url) {
      if (!url || typeof url !== 'string') return 'Uploaded file';

      const cleanUrl = url.split('?')[0].split('#')[0];
      return cleanUrl.split('/').pop() || 'Uploaded file';
    },

    handleResourcePersonFileUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;

      const isImage = file.type && file.type.startsWith('image/');
      const preview = isImage ? URL.createObjectURL(file) : '';

      this.$set(this.form.resource_persons[index], 'cv', file);
      this.$set(this.form.resource_persons[index], 'cvName', file.name);
      this.$set(this.form.resource_persons[index], 'cvPreview', preview);
    },

    removeItem(id) {
      this.form.contact_persons = this.form.contact_persons.filter((contact) => contact.id !== id);
    },

    fileSelected1(event) {
      // eslint-disable-next-line prefer-destructuring
      this.cac_registration_certificate = event.target.files[0];

      const isImage =
        this.cac_registration_certificate &&
        this.cac_registration_certificate.type &&
        this.cac_registration_certificate.type.startsWith('image/');
      this.cacRegistrationPreview =
        this.cac_registration_certificate && isImage
          ? URL.createObjectURL(this.cac_registration_certificate)
          : '';

      if (this.cac_registration_certificate instanceof Blob) {
        const reader = new FileReader();

        // When the file is successfully read
        reader.onload = (e) => {
          const fileContent = e.target.result;

          // Save the file content (Base64 string) to localStorage
          localStorage.setItem('cac_r_c', fileContent);
          localStorage.setItem('cac_r_c_name', this.cac_registration_certificate.name);
        };

        // Read the file as a Data URL (Base64 encoded)
        reader.readAsDataURL(this.cac_registration_certificate);
      } else {
        console.error('No valid file selected or the file is not a Blob');
      }
    },

    fileSelected2(event) {
      // eslint-disable-next-line prefer-destructuring
      this.cv_of_regular_contact_person = event.target.files[0];
      if (this.cv_of_regular_contact_person instanceof Blob) {
        const reader = new FileReader();

        // When the file is successfully read
        reader.onload = (e) => {
          const fileContent = e.target.result;

          // Save the file content (Base64 string) to localStorage
          localStorage.setItem('cv_cp', fileContent);
          localStorage.setItem('cv_cp_name', this.cv_of_regular_contact_person.name);
        };

        // Read the file as a Data URL (Base64 encoded)
        reader.readAsDataURL(this.cv_of_regular_contact_person);
      } else {
        console.error('No valid file selected or the file is not a Blob');
      }
    },

    fileSelected3(event) {
      // eslint-disable-next-line prefer-destructuring
      this.activities_documentation = event.target.files[0];
      if (this.activities_documentation instanceof Blob) {
        const reader = new FileReader();

        // When the file is successfully read
        reader.onload = (e) => {
          const fileContent = e.target.result;

          // Save the file content (Base64 string) to localStorage
          localStorage.setItem('activities', fileContent);
          localStorage.setItem('activities_name', this.activities_documentation.name);
        };

        // Read the file as a Data URL (Base64 encoded)
        reader.readAsDataURL(this.activities_documentation);
      } else {
        console.error('No valid file selected or the file is not a Blob');
      }
    },

    fileSelected4(event) {
      // eslint-disable-next-line prefer-destructuring
      this.umbrella_org_file = event.target.files[0];
      if (this.umbrella_org_file instanceof Blob) {
        const reader = new FileReader();

        // When the file is successfully read
        reader.onload = (e) => {
          const fileContent = e.target.result;

          // Save the file content (Base64 string) to localStorage
          localStorage.setItem('umbrella_org', fileContent);
          localStorage.setItem('umbrella_org_name', this.umbrella_org_file.name);
        };

        // Read the file as a Data URL (Base64 encoded)
        reader.readAsDataURL(this.umbrella_org_file);
      } else {
        console.error('No valid file selected or the file is not a Blob');
      }
    },
    fileSelected5(event) {
      // eslint-disable-next-line prefer-destructuring
      this.letter_of_introduction = event.target.files[0];
      if (this.letter_of_introduction instanceof Blob) {
        const reader = new FileReader();

        // When the file is successfully read
        reader.onload = (e) => {
          const fileContent = e.target.result;

          // Save the file content (Base64 string) to localStorage
          localStorage.setItem('letter_of_intro', fileContent);
          localStorage.setItem('letter_of_intro_name', this.letter_of_introduction.name);
        };

        // Read the file as a Data URL (Base64 encoded)
        reader.readAsDataURL(this.letter_of_introduction);
      } else {
        console.error('No valid file selected or the file is not a Blob');
      }
    },

    generateYear() {
      let startYear = 1940;
      const currentYear = new Date().getFullYear();
      const years = [];
      startYear = startYear || 1980;
      while (startYear <= currentYear) {
        // eslint-disable-next-line no-plusplus
        years.push(startYear++);
      }
      return years;
    },

    updateFormFields(inputForm) {
      const form = { ...inputForm }; // Create a shallow copy to avoid mutating the input
      Object.keys(form).forEach((key) => {
        if (form[key] === 'Select One') {
          form[key] = '';
        }
      });
      return form;
    },

    createEmptyFundingSource() {
      return {
        source_name: '',
        description: '',
      };
    },

    createEmptyContactPerson() {
      return {
        id: Date.now(),
        full_name: '',
        phone_number: '',
        email: '',
        address: '',
        position: '',
        cv: null,
        cvName: '',
        cvPreview: '',
      };
    },

    normalizeContactPersons(entries = []) {
      if (!Array.isArray(entries) || !entries.length) {
        return [this.createEmptyContactPerson()];
      }

      return entries.map((entry, index) => ({
        ...this.createEmptyContactPerson(),
        ...entry,
        id: entry?.id ?? Date.now() + index,
        full_name: entry?.full_name ?? entry?.first_name ?? '',
        phone_number: entry?.phone_number ?? entry?.phone ?? '',
      }));
    },

    addUmbrellaOrganization() {
      this.form.umbrella_org.push('');
    },

    removeUmbrellaOrganization(index) {
      this.form.umbrella_org.splice(index, 1);
    },

    normalizeUmbrellaOrganizations(value = this.form.umbrella_org) {
      if (Array.isArray(value)) {
        return value.length ? value : [''];
      }

      if (typeof value === 'string' && value.trim()) {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [''];
    },

    normalizeBranchStates(value = this.form.branch) {
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }

      if (typeof value === 'string' && value.trim()) {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },

    normalizeCollaborations(value = this.form.collaboration_list) {
      if (Array.isArray(value)) {
        return value.map((item) => (typeof item === 'object' ? item.name : item)).filter(Boolean);
      }

      if (typeof value === 'string' && value.trim()) {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },

    normalizeSourcesOfFunding(form = this.form) {
      if (Array.isArray(form.sources_of_funding) && form.sources_of_funding.length) {
        return form.sources_of_funding.map((source) => ({
          source_name: source?.source_name || '',
          description: source?.description || '',
        }));
      }

      if (typeof form.source_of_funding === 'string' && form.source_of_funding.trim()) {
        return [
          {
            source_name: form.source_of_funding.trim(),
            description: '',
          },
        ];
      }

      return [this.createEmptyFundingSource()];
    },

    normalizeBooleanChoice(value) {
      if (value === true || value === 'true' || value === 'yes') {
        return 'yes';
      }

      if (value === false || value === 'false' || value === 'no') {
        return 'no';
      }

      return '';
    },

    parseBooleanChoice(value) {
      if (value === 'yes') {
        return true;
      }

      if (value === 'no') {
        return false;
      }

      return value;
    },

    normalizeAreaOfFocusSelection(value = []) {
      if (!Array.isArray(value)) {
        return [];
      }

      return value
        .map((entry) => {
          if (typeof entry === 'object' && entry !== null) {
            const matched = this.programs.find((program) => program.id === entry.id);
            return matched || entry;
          }

          return this.programs.find((program) => program.id === entry) || null;
        })
        .filter(Boolean);
    },

    getAreaOfFocusIds(value = this.tempArea) {
      if (!Array.isArray(value)) {
        return [];
      }

      return value
        .map((entry) => (typeof entry === 'object' && entry !== null ? entry.id : entry))
        .filter((id) => id !== undefined && id !== null && id !== '');
    },

    normalizePublications(value = this.form.publications) {
      if (!Array.isArray(value) || !value.length) {
        return [];
      }

      return value.map((publication) => ({
        ...publication,
        name: publication?.name || '',
        link: publication?.link || publication?.sample_url || publication?.url || '',
      }));
    },

    normalizeRenewalYears(value = this.form.years_renewed) {
      const entries = Array.isArray(value) ? value : String(value || '').split(',');
      const years = entries
        .map((year) => String(year ?? '').trim())
        .filter((year) => /^\d{4}$/.test(year));
      return [...new Set(years)].sort((a, b) => Number(a) - Number(b));
    },

    prepareFormPayload() {
      const sourcesOfFunding = this.normalizeSourcesOfFunding();
      const sourceNames = sourcesOfFunding
        .map((source) => source.source_name?.trim())
        .filter(Boolean);
      const umbrellaOrganizations = this.normalizeUmbrellaOrganizations().filter(Boolean);
      const collaborations = this.normalizeCollaborations();
      const publications = this.normalizePublications().map((publication) => ({
        name: publication.name,
        link: publication.link,
      }));

      return {
        ...this.form,
        years_renewed: this.normalizeRenewalYears(),
        areas_of_focus: this.getAreaOfFocusIds(),
        sources_of_funding: sourcesOfFunding,
        source_of_funding: sourceNames.join(', ') || null,
        publications,
        collaboration_list: this.form.is_collaborative === 'yes' ? collaborations : [],
        umbrella_org:
          this.form.is_umbrella_organization === 'yes'
            ? umbrellaOrganizations.join(', ') || null
            : null,
        is_umbrella_organization: this.parseBooleanChoice(this.form.is_umbrella_organization),
        is_collaborative: this.parseBooleanChoice(this.form.is_collaborative),
      };
    },

    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key, value]) => {
          if (key === 'years_renewed' && Array.isArray(value)) return true;
          // ❌ Remove empty simple values
          if (value === '' || value === null || value === 'Select One') return false;

          // ⚠ Keep numeric 0
          if (value === 0) return true;

          // ❌ Remove empty arrays
          if (Array.isArray(value) && value.length === 0) return false;

          // ⚡ Keep file objects
          if (value instanceof File || value instanceof Blob || value instanceof FileList)
            return true;

          // 🔹 Object checks
          if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            !(value instanceof File)
          ) {
            const keys = Object.keys(value);

            // ❌ Remove empty nested objects
            if (keys.length === 0) return false;

            // ❌ Remove objects whose values are all "zero/empty"
            const allZero = keys.every((k) => {
              const v = value[k];
              return (
                v === 0 ||
                v === '0' ||
                v === '' ||
                v === null ||
                v === 'Select One' ||
                (Array.isArray(v) && v.length === 0)
              );
            });

            if (allZero) return false;
          }

          return true;
        })
      );
    },

    getMissingRequiredFields(form, fields) {
      return Object.entries(fields)
        .filter(([field]) => {
          const value = form[field];
          return (
            value === undefined ||
            value === null ||
            String(value).trim() === '' ||
            String(value).trim() === 'Select One'
          );
        })
        .map(([, label]) => label);
    },

    validateOrganizationFields(form) {
      const missingFields = this.getMissingRequiredFields(form, {
        name: 'Name of Organization',
        email: 'Email Address',
      });

      if (missingFields.length) {
        this.$toast.show({
          type: 'ERROR',
          message: `Please fill the following required fields: ${missingFields.join('; ')}.`,
        });
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email).trim())) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please enter a valid organization email address.',
        });
        return false;
      }
      return true;
    },

    toFormData(obj, formData = new FormData(), parentKey = '') {
      Object.entries(obj).forEach(([key, value]) => {
        const fieldKey = parentKey ? `${parentKey}[${key}]` : key;

        if (key === 'years_renewed' && Array.isArray(value)) {
          formData.append(fieldKey, JSON.stringify(value));
          return;
        }

        // ARRAY
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            const arrayKey = `${fieldKey}[${index}]`;

            if (item instanceof File) {
              formData.append(arrayKey, item);
            } else if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
              this.toFormData(item, formData, arrayKey);
            } else {
              // keep strings whole
              formData.append(arrayKey, item ?? '');
            }
          });
          return;
        }

        // OBJECT (but NOT File, NOT Array, NOT string)
        if (
          typeof value === 'object' &&
          value !== null &&
          !(value instanceof File) &&
          !Array.isArray(value)
        ) {
          this.toFormData(value, formData, fieldKey);
          return;
        }

        // FILE
        if (value instanceof File) {
          formData.append(fieldKey, value);
          return;
        }

        // PRIMITIVES (string, number, boolean)
        formData.append(fieldKey, value ?? '');
      });

      return formData;
    },

    submit(payload) {
      if (this.operations === 'create') {
        return this.createOrganization(payload);
      }
      return this.updateOrganization();
    },

    // async createOrganization(payload) {
    //   // -------------------------------------------------------
    //   // 1. Validate contacts (early exit)
    //   // -------------------------------------------------------
    //   if (!this.isContactComplete(this.form.contact_persons)) {
    //     return this.$toast.show({
    //       type: 'ERROR',
    //       message: 'Please fill all Contact Details',
    //     });
    //   }

    //   // -------------------------------------------------------
    //   // 2. Normalize form fields before sending
    //   // -------------------------------------------------------

    //   // Areas of focus
    //   this.form.areas_of_focus = this.tempArea?.map((item) => item.id) || [];

    //   // Collaboration list → array of names
    //   // this.form.collaboration_list = this.form.collaboration_list.map((item) => item.name);

    //   // -------------------------------------------------------
    //   // 3. Cleanup empty values, nulls, unnecessary fields
    //   // -------------------------------------------------------
    //   const cleaned = this.cleanObject({ ...this.form });

    //   console.log('CLEANED PAYLOAD →', cleaned);

    //   // -------------------------------------------------------
    //   // 4. Validate required fields before sending
    //   // -------------------------------------------------------
    //   if (!this.validateDemoForm(cleaned)) {
    //     return this.$toast.show({
    //       type: 'ERROR',
    //       message: 'Please fill all required fields',
    //     });
    //   }

    //   // -------------------------------------------------------
    //   // 5. Convert to FormData safely
    //   // -------------------------------------------------------
    //   const formData = this.toFormData(cleaned);

    //   // Debug Log (Optional)
    //   // for (const pair of formData.entries()) {
    //   //   console.log(pair[0], pair[1]);
    //   // }

    //   // -------------------------------------------------------
    //   // 6. Submit to backend
    //   // -------------------------------------------------------
    //   this.loader = true;

    //   try {
    //     await ADD_ORGANIZATION(formData, {
    //       headers: { 'Content-Type': 'multipart/form-data' },
    //     });

    //     this.$toast.show({
    //       type: 'SUCCESS',
    //       message: `${payload.name} has been created`,
    //     });

    //     this.$router.push('/ngo/landing');
    //   } catch (error) {
    //     this.$toast.show({
    //       type: 'ERROR',
    //       message: `Unable to create ${payload.name}`,
    //     });
    //   } finally {
    //     this.loader = false;
    //   }
    // },

    async createOrganization(payload) {
      const cleaned = this.cleanObject(this.prepareFormPayload());
      if (!this.validateOrganizationFields(cleaned)) return false;

      const formData = this.toFormData(cleaned);

      this.loader = true;

      try {
        await ADD_ORGANIZATION(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.$toast.show({
          type: 'SUCCESS',
          message: `${payload.name} has been created`,
        });

        this.$router.push('/ngo/landing');

        return true; // 👈 success
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: `Unable to create ${payload.name}`,
        });

        return false; // 👈 failure
      } finally {
        this.loader = false;
      }
    },

    async updateOrganization() {
      const cleaned = this.cleanObject(this.prepareFormPayload());
      if (!this.validateOrganizationFields(cleaned)) return false;

      const formData = this.toFormData(cleaned);

      console.log(formData, 'Update Data');

      // -------------------------------------------------------
      // 6. Submit
      // -------------------------------------------------------
      this.loader = true;

      try {
        const requestPayload = {
          id: cleaned.id,
          data: formData,
        };

        await updateOrganizationDetail(requestPayload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.$toast.show({
          type: 'SUCCESS',
          message: `${cleaned.name} has been updated`,
        });

        this.$router.push('/ngo/landing');

        return true; // ✅ success
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: `Unable to update ${cleaned.name}`,
        });

        return false; // ❌ failure
      } finally {
        this.loader = false;
      }
    },

    async saveOrganizationToDraft() {
      const cleaned = this.cleanObject(this.prepareFormPayload());

      if (!this.validateOrganizationFields(cleaned)) return false;

      const formData = this.toFormData(cleaned);

      this.draftLoader = true;

      try {
        await SAVE_TO_DRAFT(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.$toast.show({
          type: 'SUCCESS',
          message: `${this.form.name} has been saved to draft `,
        });
        this.$router.push('/ngo/landing');

        return true;
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to save to draft',
        });

        return false;
      } finally {
        this.draftLoader = false;
      }
    },

    // loadDraft(data) {
    //   // this.form = data;
    //   // const temp = this.getObjectsByIds(data.areas_of_focus);
    //   // this.tempArea.push(...temp);
    //   // this.selectedLocation = this.getLocationByName(data.geo_zone);
    //   console.log(data, 'M@M');

    //   this.tempArea = data.areas_of_focus;
    //   this.selectedLocation = data.geo_zone
    //     ? this.getLocationByName(data.geo_zone)
    //     : { name: null, states: [] };

    //   const temp = data;

    //   console.log(data, 'M@M 2');

    //   temp.staff_member = temp.staff_member ?? {
    //     number_of_staffs: 0,
    //     number_of_senior_staffs: 0,
    //     number_of_junior_staffs: 0,
    //     number_of_male_senior_staffs: 0,
    //     number_of_female_senior_staffs: 0,
    //     number_of_male_junior_staffs: 0,
    //     number_of_female_junior_staffs: 0,
    //     number_of_full_time_staffs: 0,
    //     number_of_part_time_staffs: 0,
    //   };

    //   this.form = {
    //     ...this.form,
    //     ...temp,
    //   };
    //   console.log(this.form, 'Abeg');

    //   // Normalize contact list
    //   // const contacts = Array.isArray(data.contact) ? data.contact : [];
    //   // this.contactList =
    //   contacts.length > 0
    //     ? contacts.map((c, index) => ({
    //         id: c.id ?? Date.now() + index, // fallback id if missing
    //         ...c,
    //       }))
    //     : [
    //         {
    //           id: Date.now(),
    //           first_name: '',
    //           last_name: '',
    //           email: '',
    //           phone: '',
    //           address: '',
    //           position: '',
    //         },
    //       ];
    // },

    hydrateOrganizationForm(data) {
      const source = { ...data };
      source.username = source.username || source?.user?.username || '';
      source.years_renewed = this.normalizeRenewalYears(source.years_renewed || []).join(', ');

      this.tempArea = this.normalizeAreaOfFocusSelection(source.areas_of_focus);
      source.areas_of_focus = this.getAreaOfFocusIds(this.tempArea);

      this.selectedLocation = source.geo_zone
        ? this.getLocationByName(source.geo_zone)
        : { name: null, states: [] };

      source.staff_member = source.staff_member ?? {
        number_of_staffs: 0,
        number_of_senior_staffs: 0,
        number_of_junior_staffs: 0,
        number_of_male_senior_staffs: 0,
        number_of_female_senior_staffs: 0,
        number_of_male_junior_staffs: 0,
        number_of_female_junior_staffs: 0,
        number_of_full_time_staffs: 0,
        number_of_part_time_staffs: 0,
      };

      this.form = {
        ...this.form,
        ...source,
      };
      this.form.branch = this.normalizeBranchStates(this.form.branch || this.form.branch_states);
      this.form.collaboration_list = this.normalizeCollaborations(this.form.collaboration_list);
      if (this.form.is_umbrella_organization || this.form.umbrella_org) {
        this.form.umbrella_org = this.normalizeUmbrellaOrganizations(this.form.umbrella_org);
      } else {
        this.form.umbrella_org = [];
      }
      this.form.is_umbrella_organization = this.normalizeBooleanChoice(
        this.form.is_umbrella_organization
      );
      this.form.is_collaborative = this.normalizeBooleanChoice(this.form.is_collaborative);
      this.form.sources_of_funding = this.normalizeSourcesOfFunding(this.form);

      // ✅ FIXED CONTACTS
      let contacts = [];

      if (Array.isArray(source.contact_persons)) {
        contacts = source.contact_persons;
      } else if (Array.isArray(source.contact)) {
        contacts = source.contact;
      }

      this.form.contact_persons = this.normalizeContactPersons(contacts);
    },

    loadDraft(data) {
      this.hydrateOrganizationForm(data);
    },

    getLocationByName(name) {
      const matchedLocation = this.location.find(
        (locationItem) => locationItem.name.toLowerCase() === name?.toLowerCase()
      );

      return matchedLocation || null;
    },

    getObjectsByIds(ids) {
      if (!Array.isArray(ids) || !Array.isArray(this.programs)) {
        throw new Error('Both arguments must be arrays.');
      }

      return this.programs.filter((obj) => ids.includes(obj.id));
    },

    async getPrograms() {
      this.program_area_loader = true;
      try {
        const data = await getProgramArea();
        this.total = data.count;
        this.programs = data.results;
      } catch (error) {
        this.programs = [];
      } finally {
        this.program_area_loader = false;
      }
    },

    // getPrograms(count) {
    //   this.program_area_loader = true;
    //   GETPROGRAMS(count)
    //     .then((data) => {
    //       this.total = data.data.count;
    //       this.programs = data.data.results;
    //       console.log(this.programs, '@');
    //     })
    //     .catch(() => {
    //       this.programs = [];
    //     })
    //     .finally(() => {
    //       this.program_area_loader = false;
    //     });
    // },
    // getSavedDraft() {
    //   this.program_area_loader = true;
    //   GET_SAVED_DRAFT()
    //     .then(({ data }) => {
    //       console.log(data.data, '@');
    //       this.savedDraft = data.data;
    //       this.loadDraft(data.data);
    //     })
    //     .catch(() => {
    //       this.savedDraft = null;
    //     })
    //     .finally(() => {
    //       this.savedDraft = null;
    //     });
    // },
    async getSavedDraft() {
      this.program_area_loader = true;
      const AUTH_TOKEN = localStorage.getItem('access_token');
      const baseURL = process.env.VUE_APP_API_BASE_URL;
      try {
        const res = await fetch(`${baseURL}organizations/draft`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(AUTH_TOKEN && { Authorization: `Bearer ${AUTH_TOKEN}` }),
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch saved draft');
        }

        const { data } = await res.json();

        this.loadDraft(data);
        this.savedDraft = data;
      } catch (error) {
        console.error('Error fetching saved draft:', error);
        this.savedDraft = null;
      } finally {
        this.program_area_loader = false;
      }

      // try {
      //   const { data } = await GET_SAVED_DRAFT();
      //   this.loadDraft(data.data);
      //   this.savedDraft = data.data;
      // } catch (error) {
      //   this.savedDraft = null;
      // } finally {
      //   this.program_area_loader = false;
      // }
    },
  },
  async mounted() {
    if (!this.isLoggedIn) {
      this.$router.push({ path: '/auth/login' });
      return;
    }

    const ID = this.$route.params.id;
    await this.getPrograms();

    if (ID === undefined) {
      this.operations = 'create';
      await this.getSavedDraft();
      const loadedStorageData = localStorage.getItem('loadedData');
      this.loadedData = !!loadedStorageData;
      if (this.loadedData && this.savedDraft) this.loadDraft(this.savedDraft);
    } else {
      this.operations = 'update';
      const { data } = await getOrganization(ID);
      this.hydrateOrganizationForm(data.data);
    }

    // Tour setup
    const tourShown = localStorage.getItem('tourShown');
    if (!tourShown) {
      localStorage.setItem('tourShown', 'Tour has been shown');
      this.steps[0].content = this.tourContentForStep1;
      this.steps[1].content = this.tourContentForStep2;
      this.steps[2].content = this.tourContentForStep3;
      this.$tours.toursguide.start();
    }
  },
};
</script>

<style lang="scss" scoped>
.program-area-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
}

.program-area-create {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 1rem 0.55rem 0.55rem;
  border: 1px solid #cce2d1;
  border-radius: 999px;
  background: #f1f8f3;
  color: #235b35;
  font-size: 0.82rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(24, 57, 31, 0.05);
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #e2f1e6;
    box-shadow: 0 6px 18px rgba(24, 57, 31, 0.1);
  }

  &:focus-visible {
    outline: 3px solid #65b67b;
    outline-offset: 3px;
  }
}

.program-area-create__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  background: #245d36;
  color: #fff;
  font-size: 0.7rem;
}

.program-area-hint {
  color: #617066;
  font-size: 0.85rem;
  line-height: 1.6;
}

.ngo-form-page {
  padding-top: 0.75rem;
  overflow-x: hidden;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: auto;
  max-width: max-content;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1.15rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(18, 43, 25, 0.06);
  color: #1f3826;
  font-weight: 700;
  line-height: 1;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.back-button:hover {
  transform: translateY(-1px);
  background: #ffffff;
  box-shadow: 0 14px 28px rgba(18, 43, 25, 0.1);
}

.back-button i {
  font-size: 0.9rem;
}

.form-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 28px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.form-hero__copy {
  max-width: 46rem;
}

.hero-kicker {
  margin-bottom: 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(247, 255, 247, 0.72);
}

.form-hero h1 {
  margin-bottom: 0.75rem;
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.1;
}

.hero-description {
  margin: 0;
  max-width: 40rem;
  line-height: 1.75;
  color: rgba(247, 255, 247, 0.82);
}

.form-hero__stats {
  display: grid;
  gap: 1rem;
  min-width: 17rem;
}

.hero-stat {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.hero-stat__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.hero-stat__value {
  font-size: 1rem;
  line-height: 1.5;
}

.form-shell {
  padding: 1.75rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 60px rgba(22, 50, 28, 0.08);
  backdrop-filter: blur(10px);
}

.sub-head {
  margin: 2rem 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
  font-family: 'Open Sans', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #163122;
}

.custom-button {
  width: fit-content;
  min-height: 3.35rem;
  padding: 0 1.35rem;
  border: 0;
  border-radius: 16px;
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
}

.upload-preview-row,
.facility-row,
.form-actions {
  gap: 1rem;
}

.form-actions {
  flex-wrap: wrap;
}
label {
  font-size: 0.9375rem;
}

* {
  font-family: 'Open Sans';
}
label {
  font-size: 0.813rem !important;
}

.ngo-form-page ::v-deep .inputBox,
.ngo-form-page ::v-deep > div {
  width: 100% !important;
}

.ngo-form-page ::v-deep .form-group {
  margin-top: 1rem !important;
  margin-bottom: 0.35rem !important;
}

.ngo-form-page ::v-deep .form-group > label {
  color: #223429 !important;
  font-size: 0.82rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ngo-form-page ::v-deep .inputField,
.ngo-form-page ::v-deep select,
.ngo-form-page textarea.form-control,
.ngo-form-page input.form-control,
.ngo-form-page input[type='file'] {
  min-height: 3.2rem !important;
  border: 1px solid rgba(28, 68, 38, 0.12) !important;
  border-radius: 16px !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.96),
    rgba(243, 248, 244, 0.92)
  ) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
  color: #1f3225 !important;
}

.ngo-form-page input[type='file'] {
  position: relative;
  width: 100%;
  padding: 0.9rem 1rem !important;
  color: transparent !important;
  cursor: pointer;
}

.ngo-form-page input[type='file']::file-selector-button {
  width: 0;
  padding: 0;
  margin: 0;
  border: 0;
  opacity: 0;
}

.ngo-form-page input[type='file']::-webkit-file-upload-button {
  width: 0;
  padding: 0;
  margin: 0;
  border: 0;
  opacity: 0;
}

.ngo-form-page input[type='file']::before {
  content: 'Upload supporting document';
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 1.25rem;
  padding-left: 1.75rem;
  color: #587066;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  background: linear-gradient(135deg, transparent 0 8px, rgba(48, 171, 76, 0.08) 8px 100%),
    radial-gradient(circle at left center, rgba(48, 171, 76, 0.14), transparent 45%);
  background-repeat: no-repeat;
  background-size: 100% 100%, 2.3rem 2.3rem;
  background-position: center, left center;
}

.ngo-form-page ::v-deep textarea,
.ngo-form-page ::v-deep .form-control[type='textarea'],
.ngo-form-page ::v-deep .textareaField,
.ngo-form-page ::v-deep textarea.form-control {
  min-height: 8rem !important;
  padding: 1rem 1rem 1.1rem !important;
  border: 1px solid rgba(28, 68, 38, 0.12) !important;
  border-radius: 18px !important;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.08), transparent 36%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(240, 247, 242, 0.94)) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 12px 24px rgba(18, 42, 24, 0.05) !important;
  color: #183024 !important;
  line-height: 1.65;
  resize: vertical;
}

.ngo-form-page ::v-deep textarea::placeholder,
.ngo-form-page ::v-deep textarea.form-control::placeholder {
  color: #8a958f;
}

.ngo-form-page ::v-deep .inputField:focus,
.ngo-form-page ::v-deep select:focus,
.ngo-form-page textarea.form-control:focus,
.ngo-form-page input.form-control:focus {
  border-color: rgba(48, 171, 76, 0.6) !important;
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1) !important;
}

.ngo-form-page ::v-deep textarea:focus,
.ngo-form-page ::v-deep textarea.form-control:focus {
  border-color: rgba(48, 171, 76, 0.7) !important;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.1), transparent 40%), #ffffff !important;
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1), 0 16px 30px rgba(18, 42, 24, 0.08) !important;
}

.ngo-form-page ::v-deep .multiselect {
  border: 0 !important;
}

.ngo-form-page ::v-deep .multiselect__tags {
  min-height: 3.2rem;
  border: 1px solid rgba(28, 68, 38, 0.12) !important;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(243, 248, 244, 0.92));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
}

.ngo-form-page ::v-deep .multiselect__tag {
  border-radius: 999px;
  background: #e8f5eb;
  color: #226c34;
}

.form-check-input {
  font-family: 'Open Sans' !important;
}

.col-mrg {
  margin-left: 40px;
}

.upload-sec {
  margin-top: 1.75rem;
}

.upload-sec--aligned {
  margin-top: 1rem;
}

.upload-label {
  display: block;
  margin-bottom: 0.45rem;
  color: #223429;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.border-radius,
.staff-count,
.ngo-form-page .card.p-5,
.ngo-form-page .mt-4.border {
  border: 1px solid rgba(28, 68, 38, 0.08) !important;
  border-radius: 24px !important;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
  box-shadow: 0 10px 28px rgba(18, 42, 24, 0.05);
}

.staff-count {
  padding: 1.5rem !important;
}

.staff-count__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.staff-count__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6d7d74;
}

.staff-count__copy {
  max-width: 24rem;
  margin: 0;
  color: #617066;
  line-height: 1.6;
  text-align: right;
}

.staff-groups {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.staff-group {
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
}

.staff-group h6 {
  margin-bottom: 0.9rem;
  font-family: 'Open Sans', sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1c3022;
}

.staff-group--summary {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.08), transparent 35%),
    linear-gradient(180deg, rgba(247, 251, 248, 0.96), rgba(239, 246, 241, 0.96));
}

.staff-fields {
  display: grid;
  gap: 0.85rem;
}

.staff-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.staff-group label,
.staff-field label {
  margin: 0;
  font-size: 0.74rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f !important;
}

.ngo-form-page .card {
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 20px;
  box-shadow: none;
}

.ngo-form-page .card-body {
  padding: 1.25rem;
}

.ngo-form-page .table {
  overflow: hidden;
  border-radius: 18px;
}

.ngo-form-page .table thead th {
  background: #eef6ef !important;
  color: #203026;
}

.ngo-form-page .btn-outline-danger {
  border-radius: 12px;
}

.ngo-form-page .btn-success.btn-sm,
.ngo-form-page .btn-primary,
.ngo-form-page .btn-success {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(48, 171, 76, 0.12);
}

.error-msg {
  margin-top: 0.45rem;
  color: #c34b4b;
  font-size: 0.84rem;
}

.image {
  height: 110px;
  width: 110px;
  border-radius: 18px;
  object-fit: cover;
}

.file-reflection {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 110px;
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
  color: #203026;
}

.file-reflection__label {
  margin-bottom: 0.35rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

@media (max-width: 991px) {
  .ngo-form-page {
    padding-top: 3.75rem;
  }

  .form-hero {
    flex-direction: column;
  }

  .form-hero__stats {
    min-width: 0;
    grid-template-columns: 1fr;
  }

  .staff-count__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .staff-count__copy {
    text-align: left;
  }

  .staff-groups {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .upload-preview-row,
  .facility-row {
    flex-direction: column;
    align-items: stretch !important;
  }

  .upload-preview-row > [class*='col-'],
  .facility-row > [class*='col-'] {
    width: 100%;
    max-width: 100%;
    padding-right: 0;
    padding-left: 0;
    margin-right: 0 !important;
  }

  .contact-action-row .btn,
  .add-contact-row .btn {
    width: 100% !important;
  }
}

@media (max-width: 767px) {
  .ngo-form-page {
    padding-top: 4.5rem;
    padding-right: 0;
    padding-left: 0;
  }

  .back-button {
    width: fit-content;
    max-width: 100%;
    justify-content: flex-start;
    padding: 0.9rem 1rem;
    text-align: left;
  }

  .form-shell,
  .form-hero {
    padding: 1rem;
    border-radius: 22px;
  }

  .form-hero h1 {
    font-size: 1.65rem;
  }

  .hero-description {
    line-height: 1.6;
  }

  .hero-stat,
  .staff-group,
  .ngo-form-page .card-body,
  .staff-count {
    padding: 1rem;
  }

  .sub-head {
    margin-top: 1.5rem;
    font-size: 1rem;
  }

  .custom-button {
    width: 100%;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch !important;
  }

  .form-actions .custom-button {
    margin-left: 0 !important;
  }

  .upload-sec {
    margin-top: 1.1rem;
  }

  .image,
  .file-reflection {
    width: 100%;
    min-height: 110px;
  }

  .image {
    height: auto;
    max-height: 220px;
  }

  .ngo-form-page input[type='file']::before {
    padding-left: 1.25rem;
    font-size: 0.84rem;
    text-align: left;
    justify-content: flex-start;
  }

  .staff-groups {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .ngo-form-page {
    padding-top: 5rem;
  }

  .ngo-form-page ::v-deep .form-group {
    margin-top: 0.75rem !important;
  }

  .form-shell,
  .form-hero,
  .border-radius,
  .staff-count,
  .ngo-form-page .card.p-5,
  .ngo-form-page .mt-4.border,
  .ngo-form-page .card,
  .file-reflection {
    border-radius: 18px !important;
  }

  .ngo-form-page .card.p-5 {
    padding: 1rem !important;
  }

  .staff-count__eyebrow,
  .hero-kicker,
  .ngo-form-page ::v-deep .form-group > label,
  .upload-label {
    letter-spacing: 0.05em;
  }
}
</style>

<!-- BootstrapVue renders the modal outside this page, so its styles use a unique global class. -->
<style lang="scss">
#ngo-create-program-area___BV_modal_backdrop_ {
  background-color: #142d1d;
  backdrop-filter: none;
  opacity: 0.2;
}

.ngo-program-modal {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 26px;
  background: #fcfdfb;
  box-shadow: 0 32px 90px rgba(12, 35, 20, 0.28);
  font-family: 'Open Sans', sans-serif;
  color: #203b2a;

  .ngo-program-modal__header {
    align-items: flex-start;
    padding: 1.75rem 1.75rem 1.5rem;
    border: 0;
    background: radial-gradient(ellipse at top right, rgba(107, 183, 119, 0.3), transparent 65%),
      linear-gradient(125deg, #143221, #245c36);
    color: #fff;

    .close {
      margin: -0.35rem -0.35rem 0 0.75rem;
      padding: 0.35rem 0.6rem;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      text-shadow: none;
      opacity: 1;
    }
  }

  &__eyebrow {
    display: block;
    margin-bottom: 0.55rem;
    color: #c0dbc7;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  &__title {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1.3;
  }

  .ngo-program-modal__body {
    padding: 1.75rem;
  }

  &__intro {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1.6rem;

    p {
      margin: 0;
      color: #647469;
      font-size: 0.85rem;
      line-height: 1.65;
    }
  }

  &__icon {
    display: flex;
    flex: 0 0 44px;
    align-items: center;
    justify-content: center;
    height: 44px;
    border: 1px solid #d9e9dc;
    border-radius: 14px;
    background: #edf5ef;
    color: #387447;
    font-size: 1.1rem;
  }

  &__field {
    margin-bottom: 1.25rem;

    label {
      display: block;
      margin-bottom: 0.55rem;
      font-size: 0.78rem;
      font-weight: 700;
      color: #2d4936;
    }

    .form-control {
      height: auto;
      padding: 0.9rem 1rem;
      border: 1px solid #dbe5dd;
      border-radius: 12px;
      background: #fff;
      color: #203b2a;
      font-size: 0.88rem;
      line-height: 1.6;
      box-shadow: inset 0 2px 4px rgba(24, 57, 31, 0.025);

      &::placeholder {
        color: #849187;
      }

      &:focus {
        border-color: #6fae7e;
        box-shadow: 0 0 0 4px rgba(65, 139, 82, 0.1);
      }

      &:disabled {
        background: #f0f3ef;
      }
    }

    textarea {
      min-height: 125px;
      resize: vertical;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
    border: 1px solid #d9e9dc;
    border-radius: 14px;
    background: #edf5ef;
    color: #245d36;

    .spinner-border {
      flex-shrink: 0;
      width: 1.5rem;
      height: 1.5rem;
      border-width: 0.18rem;
    }

    strong {
      display: block;
      font-size: 0.85rem;
    }

    p {
      margin: 0.3rem 0 0;
      color: #647469;
      font-size: 0.75rem;
      line-height: 1.6;
    }
  }

  &__note {
    display: flex;
    gap: 0.55rem;
    align-items: baseline;
    margin: 0 0 1.5rem;
    color: #647469;
    font-size: 0.75rem;
    line-height: 1.6;

    i {
      color: #438554;
    }
  }

  &__error {
    padding: 0.8rem 1rem;
    border: 1px solid #f0d2cf;
    border-radius: 12px;
    background: #fff3f1;
    color: #a3362c;
    font-size: 0.84rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding-top: 1.25rem;
    border-top: 1px solid #e5ece6;

    button {
      min-height: 46px;
      padding: 0.75rem 1.15rem;
      border: 1px solid transparent;
      border-radius: 12px;
      font-family: inherit;
      font-size: 0.82rem;
      font-weight: 700;
      transition: background 0.2s ease, box-shadow 0.2s ease;
    }
  }

  &__cancel {
    border-color: #dbe5dd !important;
    background: #fff;
    color: #526859;

    &:hover:not(:disabled) {
      background: #edf4ef;
    }
  }

  &__save {
    background: #245d36;
    color: #fff;
    box-shadow: 0 6px 16px rgba(36, 93, 54, 0.18);

    &:hover:not(:disabled) {
      background: #194829;
      box-shadow: 0 8px 20px rgba(36, 93, 54, 0.24);
    }
  }

  button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }

  button:focus-visible {
    outline: 3px solid #78b78a;
    outline-offset: 3px;
  }
}

@media (max-width: 575px) {
  .ngo-program-modal {
    border-radius: 20px;

    .ngo-program-modal__header,
    .ngo-program-modal__body {
      padding: 1.25rem;
    }

    &__title {
      font-size: 1.3rem;
    }

    &__actions button {
      flex: 1 1 auto;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .program-area-create,
  .ngo-program-modal .ngo-program-modal__actions button {
    transition: none;
  }
}
</style>
